import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import React, { ReactNode, ComponentType } from 'react';

interface ErrorBoundaryProps {
  children?: ReactNode;
  onError?: (error: Error, info: React.ErrorInfo) => void;
  FallbackComponent?: ComponentType<{
    error: Error;
    info: React.ErrorInfo;
    clearError: () => void;
  }>;
}

const DefaultErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  return <div>{children}</div>;
};

let ErrorBoundary: ComponentType<ErrorBoundaryProps> = DefaultErrorBoundary;

const shouldStartBugsnag =
  process.env.NODE_ENV !== 'development' && !!process.env.NEXT_PUBLIC_BUGSNAG_API_KEY;

if (shouldStartBugsnag) {
  Bugsnag.start({
    apiKey: process.env.NEXT_PUBLIC_BUGSNAG_API_KEY as string,
    appVersion: process.env.npm_package_version || '0.1.0',
    plugins: [new BugsnagPluginReact(React)],
  });

  const BugsnagErrorBoundary = Bugsnag.getPlugin('react')?.createErrorBoundary(React);
  if (BugsnagErrorBoundary) {
    ErrorBoundary = BugsnagErrorBoundary as ComponentType<ErrorBoundaryProps>;
  } else {
    throw new Error('Failed to initialize Bugsnag Error Boundary');
  }
} else if (process.env.NODE_ENV === 'development') {
  console.log('Bugsnag not initialized in development mode');
}

const isBugsnagStarted = () => {
  try {
    return typeof (Bugsnag as any).isStarted === 'function' && (Bugsnag as any).isStarted();
  } catch {
    return false;
  }
};

export { Bugsnag, ErrorBoundary, isBugsnagStarted };
